import React, { useState, useEffect, useContext, useRef } from 'react';
import { db } from '../utils/firebaseConfig';
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  Timestamp,
  runTransaction,
  limit,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { AuthContext } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiCalendar,
  FiClock,
  FiUser,
  FiClipboard,
  FiCheckCircle,
  FiArrowLeft,
  FiInfo,
  FiHelpCircle,
  FiStar,
  FiTarget,
  FiSave,
  FiEdit,
  FiList,
  FiTrash2,
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import ErrorModal from './ErrorModal';
import ConfirmationModal from './ConfirmationModal';
import { checkDORConstraints } from '../utils/dorValidation';
import { createReportNotification } from '../utils/notificationUtils';

// Helper function to get current year and month
const getCurrentYearMonth = () => {
  const now = new Date();
  const year = now.getFullYear().toString().slice(-2);
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  return `${year}-${month}`;
};

// Helper function to generate unique report ID
const generateUniqueReportId = async (db, reportType) => {
  const yearMonth = getCurrentYearMonth();
  const counterDocId = `${reportType}-${yearMonth}`;
  const countersRef = doc(db, 'counters', counterDocId);
  let reportId = '';

  await runTransaction(db, async (transaction) => {
    const countersDoc = await transaction.get(countersRef);
    let lastNumber = 1;
    if (!countersDoc.exists()) {
      transaction.set(countersRef, { lastNumber });
    } else {
      lastNumber = countersDoc.data().lastNumber + 1;
      transaction.update(countersRef, { lastNumber });
    }
    const lastNumberStr = lastNumber.toString().padStart(5, '0');
    reportId = `${yearMonth}-${reportType}-${lastNumberStr}`;
  });

  return reportId;
};

// Performance questions list
const performanceQuestionsList = [
  {
    id: 'A1',
    categoryLabel: 'A. Appearance',
    question: 'General appearance, weight, uniform, hygiene, hair, etc.',
  },
  {
    id: 'B1',
    categoryLabel: 'B. Job Ready',
    question: 'Forms/Equipment',
  },
  {
    id: 'C1',
    categoryLabel: 'C. Attitude',
    question: 'Acceptance of constructive criticism - verbal behavior',
  },
  {
    id: 'C2',
    categoryLabel: 'C. Attitude',
    question: 'Attitude toward police service',
  },
  {
    id: 'C3',
    categoryLabel: 'C. Attitude',
    question: 'Responds promptly to calls',
  },
  {
    id: 'D1',
    categoryLabel: 'D. Knowledge',
    question: 'Driving Skills - Normal',
  },
  {
    id: 'D2',
    categoryLabel: 'D. Knowledge',
    question: 'High speed, stress conditions',
  },
  {
    id: 'D3',
    categoryLabel: 'D. Knowledge',
    question: 'Knowledge of the town and wards',
  },
  {
    id: 'D4',
    categoryLabel: 'D. Knowledge',
    question: 'Report writing organization, completeness, grammar, and spelling',
  },
  {
    id: 'D5',
    categoryLabel: 'D. Knowledge',
    question: 'Appropriate use of time relevant to investigation and report writing',
  },
  {
    id: 'D6',
    categoryLabel: 'D. Knowledge',
    question: 'Accident investigation to include collection of physical evidence, field sketch, and measurements',
  },
  {
    id: 'D7',
    categoryLabel: 'D. Knowledge',
    question: 'Self-motivation/self-initiated police action',
  },
  {
    id: 'D8',
    categoryLabel: 'D. Knowledge',
    question: 'Proper use of the police radio etiquette, 10 codes and signals',
  },
  {
    id: 'F1',
    categoryLabel: 'F. Relationships',
    question: 'With Citizens',
  },
  {
    id: 'F2',
    categoryLabel: 'F. Relationships',
    question: 'Cross-cultural',
  },
  {
    id: 'F3',
    categoryLabel: 'F. Relationships',
    question: 'With FTO',
  },
  {
    id: 'F4',
    categoryLabel: 'F. Relationships',
    question: 'With squad members',
  },
  {
    id: 'F5',
    categoryLabel: 'F. Relationships',
    question: 'With supervisors',
  },
  {
    id: 'G1',
    categoryLabel: 'G. Dependability',
    question: 'Report to work on time',
  },
  {
    id: 'G2',
    categoryLabel: 'G. Dependability',
    question: 'Use of sick leave',
  },
  {
    id: 'H1',
    categoryLabel: 'H. Interview/Interrogation Skills',
    question: 'Ability to use proper questioning techniques',
  },
  {
    id: 'J1',
    categoryLabel: 'J. Control of Conflict',
    question: 'Use of appropriate levels of force',
  },
];

const CreateDOR = () => {
  const { currentUser, role } = useContext(AuthContext);
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const [showHelpText, setShowHelpText] = useState(false);

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isDraftModalOpen, setIsDraftModalOpen] = useState(false);
  const [isDraftSelectorModalOpen, setIsDraftSelectorModalOpen] = useState(false);
  const [availableDrafts, setAvailableDrafts] = useState([]);
  const [trainees, setTrainees] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [loadingDraft, setLoadingDraft] = useState(true);
  const [isDeleteDraftModalOpen, setIsDeleteDraftModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    date: (() => {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    })(),
    time: (() => {
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    })(),
    shiftLength: '12',
    traineeId: '',
    performanceQuestions: performanceQuestionsList.map((item) => ({
      ...item,
      rating: '3',
      explanation: '',
    })),
    positiveObservations: '',
    negativeObservations: '',
    recommendations: '',
    comments: [],
  });

  useEffect(() => {
    const fetchUsers = async () => {
      if (role !== 'trainer' && role !== 'admin') {
        navigate('/');
        return;
      }

      try {
        const traineeQuery = query(
          collection(db, 'users'),
          where('role', '==', 'trainee'),
          where('archived', '==', false)
        );
        const traineeSnapshot = await getDocs(traineeQuery);
        const traineeList = traineeSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTrainees(traineeList);
      } catch (error) {
        console.error('Error fetching trainees:', error);
        toast.error('Failed to load trainees');
      }
    };

    fetchUsers();
  }, [role, navigate]);

  // Check for existing draft when component mounts
  useEffect(() => {
    const checkForDraft = async () => {
      if (!currentUser) return;
      
      try {
        const draftsQuery = query(
          collection(db, 'dorDrafts'),
          where('createdBy', '==', currentUser.uid)
        );
        
        const draftSnapshot = await getDocs(draftsQuery);
        
        if (!draftSnapshot.empty) {
          // Store all available drafts
          const drafts = draftSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          setAvailableDrafts(drafts);
          
          // If there's only one draft, show the regular draft modal
          if (drafts.length === 1) {
            setDraftId(drafts[0].id);
            setIsDraftModalOpen(true);
          } 
          // If there are multiple drafts, show the draft selector modal
          else if (drafts.length > 1) {
            setIsDraftSelectorModalOpen(true);
          }
        }
      } catch (error) {
        console.error('Error checking for drafts:', error);
      } finally {
        setLoadingDraft(false);
      }
    };
    
    checkForDraft();
  }, [currentUser]);

  const loadDraft = async (draftIdToLoad, showToast = true) => {
    // Use the provided draftId or fall back to the state variable
    const idToLoad = draftIdToLoad || draftId;
    
    if (!idToLoad) {
      return;
    }
    
    try {
      console.log('Loading draft with ID:', idToLoad);
      const draftDocRef = doc(db, 'dorDrafts', idToLoad);
      const draftSnapshot = await getDoc(draftDocRef);
      
      if (draftSnapshot.exists()) {
        const draftData = draftSnapshot.data();
        
        // If we're loading a specific draft ID, update the state
        if (draftIdToLoad && draftIdToLoad !== draftId) {
          setDraftId(draftIdToLoad);
        }
        
        // Convert Firestore timestamps back to string format for form inputs
        let formattedData = { ...draftData };
        
        if (draftData.date) {
          // Check if date is a Firestore Timestamp
          if (draftData.date.toDate && typeof draftData.date.toDate === 'function') {
            const date = draftData.date.toDate();
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            formattedData.date = `${year}-${month}-${day}`;
          } else if (typeof draftData.date === 'string') {
            // If it's already a string in YYYY-MM-DD format, use it directly
            formattedData.date = draftData.date;
          }
        }
        
        if (draftData.time) {
          // Check if time is a Firestore Timestamp
          if (draftData.time.toDate && typeof draftData.time.toDate === 'function') {
            const date = draftData.time.toDate();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            formattedData.time = `${hours}:${minutes}`;
          } else if (typeof draftData.time === 'string') {
            // If it's already a string in HH:MM format, use it directly
            formattedData.time = draftData.time;
          }
        }
        
        // Ensure performanceQuestions has the correct structure
        if (!formattedData.performanceQuestions) {
          console.log('No performanceQuestions in draft, initializing with default values');
          formattedData.performanceQuestions = performanceQuestionsList.map((item) => ({
            ...item,
            rating: '3',
            explanation: '',
          }));
        } else if (Array.isArray(formattedData.performanceQuestions)) {
          console.log('performanceQuestions exists, ensuring correct structure');
          // Ensure all required fields are present in each question
          formattedData.performanceQuestions = formattedData.performanceQuestions.map(question => {
            const baseQuestion = performanceQuestionsList.find(q => q.id === question.id) || {};
            return {
              ...baseQuestion,
              ...question,
              rating: question.rating || '3',
              explanation: question.explanation || '',
            };
          });
        }
        
        // Ensure all required fields are present
        const defaultFormData = {
          date: formattedData.date || (() => {
            const today = new Date();
            return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
          })(),
          time: formattedData.time || (() => {
            const now = new Date();
            return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
          })(),
          shiftLength: formattedData.shiftLength || '12',
          traineeId: formattedData.traineeId || '',
          performanceQuestions: formattedData.performanceQuestions,
          positiveObservations: formattedData.positiveObservations || '',
          negativeObservations: formattedData.negativeObservations || '',
          recommendations: formattedData.recommendations || '',
          comments: formattedData.comments || [],
          location: formattedData.location || '',
          activity: formattedData.activity || '',
        };
        
        setFormData(defaultFormData);
        
        // Only show toast if requested
        if (showToast) {
          toast.info('Draft loaded successfully');
        }
      } else {
        console.log('Draft document does not exist');
        if (showToast) {
          toast.error('Draft not found');
        }
      }
    } catch (error) {
      console.error('Error loading draft:', error);
      if (showToast) {
        toast.error('Failed to load draft');
      }
    }
  };

  const selectDraft = async (draftId) => {
    console.log('Selecting draft with ID:', draftId);
    
    // Set the draft ID and close the modal
    setDraftId(draftId);
    setIsDraftSelectorModalOpen(false);
    
    // Load the draft with the specific ID
    await loadDraft(draftId);
    
    console.log('Draft selection complete');
  };

  const startFresh = () => {
    setDraftId(null);
    setIsDraftModalOpen(false);
    setIsDraftSelectorModalOpen(false);
    
    // Reset form data to default values
    setFormData({
      date: (() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      })(),
      time: (() => {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
      })(),
      shiftLength: '12',
      traineeId: '',
      location: '',
      activity: '',
      performanceQuestions: performanceQuestionsList.map((item) => ({
        ...item,
        rating: '3',
        explanation: '',
      })),
      positiveObservations: '',
      negativeObservations: '',
      recommendations: '',
      comments: [],
      additionalComments: ''
    });
  };

  const handleSaveDraft = async () => {
    if (!currentUser) {
      toast.error('You must be logged in to save a draft');
      return;
    }
    
    if (!formData.traineeId) {
      toast.warning('Please select a trainee before saving a draft');
      return;
    }
    
    setSavingDraft(true);
    
    try {
      // Prepare draft data - store dates as strings to avoid conversion issues
      const draftData = {
        ...formData,
        createdBy: currentUser.uid,
        lastUpdated: Timestamp.now(),
        // Ensure date and time are stored as strings
        date: formData.date,
        time: formData.time
      };
      
      // If we already have a draft ID, update it, otherwise create a new one
      if (draftId) {
        const draftRef = doc(db, 'dorDrafts', draftId);
        await updateDoc(draftRef, draftData);
      } else {
        const draftRef = collection(db, 'dorDrafts');
        const newDraftDoc = await addDoc(draftRef, draftData);
        setDraftId(newDraftDoc.id);
      }
      
      toast.success('Draft saved successfully');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleDeleteDraft = () => {
    setIsDeleteDraftModalOpen(true);
  };

  const confirmDeleteDraft = async () => {
    if (!draftId) return;
    
    try {
      const draftRef = doc(db, 'dorDrafts', draftId);
      await deleteDoc(draftRef);
      setDraftId(null);
      setIsDeleteDraftModalOpen(false);
      toast.success('Draft deleted successfully');
    } catch (error) {
      console.error('Error deleting draft:', error);
      toast.error('Failed to delete draft');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePerformanceChange = (id, value) => {
    setFormData((prev) => ({
      ...prev,
      performanceQuestions: prev.performanceQuestions.map((item) =>
        item.id === id ? { ...item, rating: value } : item
      ),
    }));
  };

  const handleExplanationChange = (id, value) => {
    // Limit explanation to 120 characters
    if (value.length > 120) return;
    setFormData((prev) => ({
      ...prev,
      performanceQuestions: prev.performanceQuestions.map((item) =>
        item.id === id ? { ...item, explanation: value } : item
      ),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!currentUser || (role !== 'admin' && role !== 'trainer')) {
      toast.error('You do not have permission to create a DOR.');
      return;
    }

    setIsConfirmModalOpen(true);
    // No need for manual scrolling anymore as the modal is now properly positioned
  };

  const handleConfirm = async () => {
    setIsConfirmModalOpen(false);
    setSubmitting(true);

    try {
      // Parse the date string - keep it in local timezone
      const [year, month, day] = formData.date.split('-').map(Number);
      const selectedDate = new Date(year, month - 1, day);

      // Check constraints
      const constraintCheck = await checkDORConstraints(db, formData.traineeId, selectedDate);

      if (!constraintCheck.allowed) {
        setIsErrorModalOpen(true);
        toast.warning(constraintCheck.message);
        setSubmitting(false);
        return;
      }

      // For the actual DOR record, create a date with the specific time in local timezone
      const [hours, minutes] = formData.time.split(':').map(Number);
      const reportDateTime = new Date(year, month - 1, day, hours, minutes);

      // Create a date-only version for querying - normalized to midnight local time
      const dateOnlyDate = new Date(year, month - 1, day, 0, 0, 0, 0);

      // Check if a DOR for this trainee on the selected date already exists
      // First try with dateOnly field (for newer DORs)
      const existingDORQuery = query(
        collection(db, 'dors'),
        where('traineeId', '==', formData.traineeId),
        where('dateOnly', '==', Timestamp.fromDate(dateOnlyDate)),
        limit(1)
      );

      const existingDORSnapshot = await getDocs(existingDORQuery);
      
      // If no results with dateOnly, check using date field for older DORs
      if (existingDORSnapshot.empty) {
        // Create date range for the entire day in local timezone
        const startOfDay = new Date(year, month - 1, day, 0, 0, 0);
        const endOfDay = new Date(year, month - 1, day, 23, 59, 59, 999);
        
        const fallbackQuery = query(
          collection(db, 'dors'),
          where('traineeId', '==', formData.traineeId),
          where('date', '>=', Timestamp.fromDate(startOfDay)),
          where('date', '<=', Timestamp.fromDate(endOfDay)),
          limit(1)
        );
        
        const fallbackSnapshot = await getDocs(fallbackQuery);
        if (!fallbackSnapshot.empty) {
          toast.error('A DOR for this trainee on the selected date already exists.');
          setSubmitting(false);
          return;
        }
      } else {
        toast.error('A DOR for this trainee on the selected date already exists.');
        setSubmitting(false);
        return;
      }

      // Validate explanations for ratings of 1 or 5
      const invalidExplanations = formData.performanceQuestions.filter(
        (item) =>
          (item.rating === '1' || item.rating === '5') &&
          !item.explanation.trim()
      );

      if (invalidExplanations.length > 0) {
        toast.error(
          'Explanations for ratings of 1 or 5 must be provided.'
        );
        setSubmitting(false);
        return;
      }

      // Generate report ID (custom human-readable ID)
      const reportId = await generateUniqueReportId(db, 'DOR');

      // Fetch trainee data
      const traineeDocRef = doc(db, 'users', formData.traineeId);
      const traineeDocSnap = await getDoc(traineeDocRef);
      if (!traineeDocSnap.exists()) {
        toast.error('Selected trainee does not exist.');
        setSubmitting(false);
        return;
      }
      const traineeData = traineeDocSnap.data();

      // Fetch trainer data
      const trainerDocRef = doc(db, 'users', currentUser.uid);
      const trainerDocSnap = await getDoc(trainerDocRef);
      if (!trainerDocSnap.exists()) {
        toast.error('Trainer does not exist.');
        setSubmitting(false);
        return;
      }
      const trainerData = trainerDocSnap.data();

      // Prepare and save DOR data
      const dorData = {
        ...formData,
        reportId,
        date: Timestamp.fromDate(reportDateTime),
        dateOnly: Timestamp.fromDate(dateOnlyDate),
        createdBy: currentUser.uid,
        trainerName: trainerData.name || 'Unknown',
        trainerIdNumber: trainerData.idNumber || '',
        traineeName: traineeData.name || 'Unknown',
        traineeIdNumber: traineeData.idNumber || '',
        createdAt: Timestamp.fromDate(new Date()),
        submissionDate: Timestamp.fromDate(new Date()),
        archived: traineeData.archived || false,
      };

      // Save the DOR to Firestore
      const docRef = await addDoc(collection(db, 'dors'), dorData);
      
      // Create notification for the trainee
      await createReportNotification(
        formData.traineeId,
        traineeData.name || 'Unknown',
        trainerData.name || 'Unknown',
        'DOR',
        reportId,
        docRef.id,
        reportDateTime
      );

      toast.success(`DOR created successfully! Report ID: ${reportId}`);
      navigate('/dors');

      // If submission is successful and we had a draft, delete it
      if (draftId) {
        try {
          const draftRef = doc(db, 'dorDrafts', draftId);
          await deleteDoc(draftRef);
          setDraftId(null);
        } catch (error) {
          console.error('Error deleting draft after submission:', error);
        }
      }
    } catch (error) {
      console.error('Error creating DOR:', error);
      toast.error('Failed to create DOR. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (role !== 'admin' && role !== 'trainer') {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <div className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 rounded-lg shadow-md flex items-center">
          <FiInfo className="mr-2 flex-shrink-0" size={20} />
          <span>Access Denied - You don't have permission to create Daily Observation Reports</span>
        </div>
      </div>
    );
  }

  const inputStyle = `
    w-full 
    p-3 
    rounded-md 
    bg-secondary-light/30 
    dark:bg-secondary-dark 
    border 
    border-secondary-light 
    dark:border-secondary-dark 
    focus:ring-2 
    focus:ring-accent-light 
    dark:focus:ring-accent-dark 
    focus:border-transparent 
    outline-none 
    transition-all 
    duration-200
    text-text-light
    dark:text-white
  `;

  // Group performance questions by category
  const groupedPerformanceQuestions = {};
  formData.performanceQuestions.forEach((item) => {
    if (!groupedPerformanceQuestions[item.categoryLabel]) {
      groupedPerformanceQuestions[item.categoryLabel] = [];
    }
    groupedPerformanceQuestions[item.categoryLabel].push(item);
  });

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="p-4 sm:p-6 md:p-8 bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark transition-colors duration-300"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="max-w-4xl mx-auto"
        >
          {/* Header with back button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          >
            <button 
              onClick={() => navigate('/dors')}
              className="inline-flex items-center text-sm font-medium text-accent-light dark:text-accent-dark hover:text-accent-dark dark:hover:text-accent-light transition-colors duration-200"
            >
              <FiArrowLeft className="mr-2" />
              Back to Reports
            </button>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex items-center gap-2 sm:gap-4"
            >
              {!loadingDraft && (
                <>
                  <button
                    onClick={handleSaveDraft}
                    disabled={savingDraft}
                    className="inline-flex items-center text-xs sm:text-sm font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors duration-200"
                  >
                    <FiSave className="mr-1 sm:mr-2" />
                    <span className="whitespace-nowrap">
                      {savingDraft ? 'Saving...' : (draftId ? 'Update Draft' : 'Save Draft')}
                    </span>
                  </button>
                  
                  {draftId && (
                    <button
                      onClick={handleDeleteDraft}
                      disabled={submitting || savingDraft}
                      className="inline-flex items-center text-xs sm:text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors duration-200"
                    >
                      <FiTrash2 className="mr-1 sm:mr-2" />
                      <span className="whitespace-nowrap">Delete Draft</span>
                    </button>
                  )}
                </>
              )}
              
              <button
                onClick={() => setShowHelpText(!showHelpText)}
                className="inline-flex items-center text-xs sm:text-sm font-medium text-accent-light dark:text-accent-dark hover:text-accent-dark dark:hover:text-accent-light transition-colors duration-200"
              >
                <FiHelpCircle className="mr-1 sm:mr-2" />
                <span className="whitespace-nowrap">{showHelpText ? 'Hide Help' : 'Show Help'}</span>
              </button>
            </motion.div>
          </motion.div>

          {/* Help text */}
          {showHelpText && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-6 p-4 bg-secondary-light/30 dark:bg-secondary-dark/30 rounded-lg border border-secondary-light dark:border-secondary-dark"
            >
              <h3 className="flex items-center text-lg font-semibold mb-2">
                <FiInfo className="mr-2 text-accent-light dark:text-accent-dark" />
                About Daily Observation Reports
              </h3>
              <p className="text-sm md:text-base">
                Daily Observation Reports (DORs) are essential tools for tracking a trainee's daily performance and progress.
                These reports provide detailed evaluations across multiple performance categories and help identify areas for improvement.
              </p>
              <ul className="mt-3 list-disc list-inside text-sm md:text-base space-y-1">
                <li>Select the date and time of the observation period</li>
                <li>Choose the trainee you're evaluating</li>
                <li>Rate each performance category from 1 (Poor) to 5 (Excellent)</li>
                <li>Provide explanations for any ratings of 1 or 5</li>
                <li>Document positive and negative observations</li>
                <li>Include specific recommendations for improvement</li>
              </ul>
              <p className="mt-3 text-sm md:text-base font-medium">
                Note: After 5 consecutive DORs, you'll need to complete an End of Week Report before creating additional DORs.
              </p>
            </motion.div>
          )}

          {/* Main form card */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
            className="bg-primary-light dark:bg-primary-dark rounded-lg shadow-lg border border-secondary-light dark:border-secondary-dark overflow-hidden transition-all duration-300"
          >
            {/* Card header */}
            <div className="p-4 sm:p-6 border-b border-secondary-light dark:border-secondary-dark bg-secondary-light/10 dark:bg-secondary-dark/10">
              <h1 className="text-xl sm:text-2xl font-bold flex items-center">
                <FiClipboard className="mr-3 text-accent-light dark:text-accent-dark" />
                {draftId ? 'Edit Draft DOR' : 'Create Daily Observation Report'}
              </h1>
              {draftId && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-text-light/70 dark:text-text-dark/70 mt-2">
                    <FiEdit className="inline mr-1" /> You are working on a saved draft
                  </p>
                </div>
              )}
            </div>

            {/* Form content */}
            <div className="p-4 sm:p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Date, Time and Shift Length Section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="block font-medium flex items-center text-sm">
                      <FiCalendar className="mr-2 text-accent-light dark:text-accent-dark" />
                      Date
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      className={inputStyle}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium flex items-center text-sm">
                      <FiClock className="mr-2 text-accent-light dark:text-accent-dark" />
                      Time
                    </label>
                    <input
                      type="time"
                      name="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      className={inputStyle}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium flex items-center text-sm">
                      <FiClock className="mr-2 text-accent-light dark:text-accent-dark" />
                      Shift Length
                    </label>
                    <select
                      name="shiftLength"
                      value={formData.shiftLength}
                      onChange={handleInputChange}
                      className={inputStyle}
                    >
                      <option value="8">8 hours</option>
                      <option value="10">10 hours</option>
                      <option value="12">12 hours</option>
                    </select>
                  </div>
                </div>

                {/* Trainee Selection */}
                <div className="space-y-2">
                  <label className="block font-medium flex items-center text-sm">
                    <FiUser className="mr-2 text-accent-light dark:text-accent-dark" />
                    Select Trainee
                  </label>
                  <select
                    name="traineeId"
                    value={formData.traineeId}
                    onChange={handleInputChange}
                    className={inputStyle}
                    required
                  >
                    <option value="">Select a trainee</option>
                    {trainees.map((trainee) => (
                      <option key={trainee.id} value={trainee.id}>
                        {trainee.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Performance Evaluation Section */}
                <div className="mt-8">
                  <h2 className="text-lg font-semibold mb-4 flex items-center">
                    <FiStar className="mr-2 text-accent-light dark:text-accent-dark" />
                    Performance Evaluation
                  </h2>
                  <div className="space-y-8">
                    {Object.entries(groupedPerformanceQuestions).map(
                      ([categoryLabel, items]) => (
                        <div key={categoryLabel} className="p-4 bg-secondary-light/10 dark:bg-secondary-dark/10 rounded-lg border border-secondary-light/30 dark:border-secondary-dark/30">
                          <h3 className="text-md font-semibold mb-3 text-accent-light dark:text-accent-dark">{categoryLabel}</h3>
                          <div className="space-y-4">
                            {items.map((questionItem) => (
                              <div key={questionItem.id} className="p-3 bg-secondary-light/5 dark:bg-secondary-dark/5 rounded-md">
                                <label className="block mb-2 text-sm">{questionItem.question}</label>
                                <select
                                  value={questionItem.rating}
                                  onChange={(e) =>
                                    handlePerformanceChange(questionItem.id, e.target.value)
                                  }
                                  className={inputStyle}
                                >
                                  <option value="1">1 - Poor</option>
                                  <option value="2">2 - Below Average</option>
                                  <option value="3">3 - Average</option>
                                  <option value="4">4 - Above Average</option>
                                  <option value="5">5 - Excellent</option>
                                </select>
                                {(questionItem.rating === '1' || questionItem.rating === '5') && (
                                  <div className="mt-3">
                                    <label className="block mb-1 text-sm">
                                      Please explain (maximum 120 characters):
                                    </label>
                                    <textarea
                                      value={questionItem.explanation}
                                      onChange={(e) =>
                                        handleExplanationChange(questionItem.id, e.target.value)
                                      }
                                      className={inputStyle}
                                      rows="2"
                                      required
                                    />
                                    <div className="text-right text-xs text-text-light/60 dark:text-text-dark/60 mt-1">
                                      {questionItem.explanation.length} / 120 characters
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>

                {/* Observations and Recommendations */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div className="space-y-2">
                    <label className="block font-medium flex items-center text-sm">
                      <FiStar className="mr-2 text-accent-light dark:text-accent-dark" />
                      Positive Observations
                    </label>
                    <textarea
                      name="positiveObservations"
                      value={formData.positiveObservations}
                      onChange={handleInputChange}
                      className={inputStyle}
                      rows="4"
                      required
                      placeholder="Describe positive observations here..."
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium flex items-center text-sm">
                      <FiStar className="mr-2 text-accent-light dark:text-accent-dark" />
                      Negative Observations
                    </label>
                    <textarea
                      name="negativeObservations"
                      value={formData.negativeObservations}
                      onChange={handleInputChange}
                      className={inputStyle}
                      rows="4"
                      required
                      placeholder="Describe negative observations here..."
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block font-medium flex items-center text-sm">
                    <FiTarget className="mr-2 text-accent-light dark:text-accent-dark" />
                    Recommendations
                  </label>
                  <textarea
                    name="recommendations"
                    value={formData.recommendations}
                    onChange={handleInputChange}
                    className={inputStyle}
                    rows="4"
                    required
                    placeholder="Provide specific recommendations for improvement..."
                  />
                </div>

                {/* Form Actions */}
                <div className="p-4 sm:p-6 border-t border-secondary-light dark:border-secondary-dark bg-secondary-light/5 dark:bg-secondary-dark/5 flex flex-wrap justify-end gap-3">
                  {availableDrafts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setIsDraftSelectorModalOpen(true)}
                      className="flex-grow sm:flex-grow-0 sm:w-auto px-4 py-2 rounded-md flex items-center justify-center font-medium
                        bg-blue-500/90 dark:bg-blue-600/90
                        text-white
                        hover:bg-blue-600 dark:hover:bg-blue-700
                        focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 dark:focus:ring-blue-600
                        transition-all duration-200 text-sm"
                    >
                      <FiList className="mr-1.5" />
                      Switch Draft
                    </button>
                  )}
                  
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    className="flex-grow sm:flex-grow-0 sm:w-auto px-5 py-2 rounded-md flex items-center justify-center font-medium
                      bg-accent-light dark:bg-accent-dark
                      text-primary-light dark:text-primary-dark
                      hover:brightness-110
                      focus:ring-2 focus:ring-offset-1 focus:ring-accent-light dark:focus:ring-accent-dark
                      disabled:opacity-70 disabled:cursor-not-allowed
                      transition-all duration-200 text-sm"
                    disabled={submitting || savingDraft}
                  >
                    <FiCheckCircle className="mr-1.5" />
                    {submitting ? 'Submitting...' : 'Submit Report'}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Error Modal */}
      <ErrorModal
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        onAction={() => {
          setIsErrorModalOpen(false);
          navigate('/create-eowr');
        }}
        message="You have reached the maximum limit of 5 consecutive Daily Observation Reports. You must create an End of Week Report before continuing with new DORs."
        actionText="Create End of Week Report"
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        ref={modalRef}
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirm}
        title="Confirm DOR Creation"
        message="Are you sure you want to create this Daily Observation Report?"
        confirmText="Create Report"
        cancelText="Cancel"
      />

      {/* Draft Modal */}
      <ConfirmationModal
        isOpen={isDraftModalOpen}
        onClose={() => {
          startFresh();
        }}
        onConfirm={() => {
          loadDraft(draftId);
          setIsDraftModalOpen(false);
        }}
        title="Draft Found"
        message="You have a saved draft for a Daily Observation Report. Would you like to load it and continue editing?"
        confirmText="Load Draft"
        cancelText="Start Fresh"
      />

      {/* Draft Selector Modal */}
      {isDraftSelectorModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70">
          <div className="bg-primary-light dark:bg-primary-dark p-6 rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto border border-secondary-light dark:border-secondary-dark">
            <h2 className="text-xl font-bold mb-4 text-text-light dark:text-text-dark">
              Multiple Drafts Found
            </h2>
            <p className="mb-4 text-text-light/70 dark:text-text-dark/70 text-sm">
              You have multiple saved drafts for different trainees. Please select one to edit or start fresh.
            </p>
            
            <div className="space-y-3 mb-4">
              {availableDrafts.map(draft => {
                const trainee = trainees.find(t => t.id === draft.traineeId);
                const traineeName = trainee ? trainee.name : 'Unknown Trainee';
                
                let lastUpdated = 'Unknown date';
                if (draft.lastUpdated && draft.lastUpdated.toDate) {
                  const date = draft.lastUpdated.toDate();
                  lastUpdated = date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
                }
                
                return (
                  <div 
                    key={draft.id} 
                    className="border border-secondary-light dark:border-secondary-dark rounded-lg p-3 
                      hover:bg-secondary-light/10 dark:hover:bg-secondary-dark/10 cursor-pointer
                      transition-colors duration-200
                      bg-secondary-light/5 dark:bg-secondary-dark/5"
                    onClick={() => selectDraft(draft.id)}
                  >
                    <div className="font-semibold text-text-light dark:text-text-dark">
                      {traineeName}
                    </div>
                    <div className="text-sm text-text-light/70 dark:text-text-dark/70">
                      Last updated: {lastUpdated}
                    </div>
                    <div className="text-xs text-text-light/50 dark:text-text-dark/50 mt-1">
                      Draft ID: {draft.id.substring(0, 8)}...
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="flex justify-between gap-3">
              <button
                className="px-4 py-2 rounded-md
                  bg-secondary-light dark:bg-secondary-dark
                  text-text-light dark:text-text-dark
                  hover:bg-secondary-light/80 dark:hover:bg-secondary-dark/80
                  transition-colors duration-200"
                onClick={startFresh}
              >
                Start Fresh
              </button>
              <button
                className="px-4 py-2 rounded-md
                  bg-accent-light dark:bg-accent-dark
                  text-primary-light dark:text-primary-dark
                  hover:brightness-110
                  transition-all duration-200"
                onClick={() => setIsDraftSelectorModalOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Draft Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteDraftModalOpen}
        onClose={() => setIsDeleteDraftModalOpen(false)}
        onConfirm={confirmDeleteDraft}
        title="Confirm Delete Draft"
        message="Are you sure you want to delete this draft? This action cannot be undone."
        confirmText="Delete Draft"
        cancelText="Cancel"
        confirmButtonColor="red"
      />
    </>
  );
};

export default CreateDOR;
